import LegalLayout from './LegalLayout'
import { Shield } from 'lucide-react'


export default function TermsPage() {
  return (
    <LegalLayout
      title="Términos y Condiciones de Servicio"
      subtitle="Reglas y condiciones aplicables al uso de la plataforma IridiaChat y sus servicios conectados"
      lastUpdated="15 de octubre de 2026"
    >
      <div className="space-y-8 text-slate-700 leading-relaxed text-sm sm:text-base">
        {/* Intro */}
        <section className="space-y-3">
          <p>
            Bienvenido a <strong>IridiaChat</strong> (en adelante, la "Plataforma" o "el Servicio"), accesible a través del dominio oficial <strong>https://iridiachat.com</strong>. Los presentes Términos y Condiciones de Servicio constituyen un contrato legalmente vinculante entre la persona natural o jurídica que contrata o utiliza el Servicio (en adelante, "el Cliente", "la Empresa" o "el Usuario") y la sociedad mercantil <strong>INVERSIONES MERCADO NARANJA, C.A.</strong> (RIF: J-502314539), titular y operadora legítima de IridiaChat.
          </p>
          <p>
            Al registrarse, acceder o utilizar IridiaChat, usted declara que posee capacidad legal suficiente para obligarse y que acepta expresamente todos los términos aquí estipulados. Si no está de acuerdo con alguno de ellos, debe abstenerse de utilizar la Plataforma.
          </p>
        </section>

        {/* 1. Titularidad y Descripción del Servicio */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">1</span>
            Titularidad Legal y Descripción del Servicio
          </h2>
          <p>
            IridiaChat es una plataforma SaaS de mensajería multicanal, orquestación conversacional, catálogos digitales e integración de automatización con inteligencia artificial y atención en equipo para empresas, desarrollada y operada por <strong>INVERSIONES MERCADO NARANJA, C.A.</strong>, sociedad mercantil constituida e inscrita ante el Registro Mercantil Primero del Distrito Capital bajo el N° 7, Tomo 304-A, Expediente 220-70295, titular del RIF N° J-502314539 y domiciliada en Caracas, Venezuela.
          </p>
          <p>
            El Servicio permite conectar canales oficiales de comunicación, tales como <strong>WhatsApp Business Cloud API</strong> (de Meta Platforms, Inc.), Telegram y WebChat, facilitando la gestión centralizada de mensajes y la atención al cliente conforme a las políticas vigentes de cada proveedor.
          </p>
        </section>

        {/* 2. Cumplimiento de Políticas de Meta y WhatsApp */}
        <section className="space-y-3 bg-emerald-50/60 border border-emerald-200/80 rounded-xl p-5">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-700" />
            2. Cumplimiento Estricto de las Políticas de Meta y WhatsApp
          </h2>
          <p className="text-slate-800 font-medium">
            Al conectar su cuenta de WhatsApp Business (WABA) a IridiaChat, el Cliente se compromete de manera irrevocable a cumplir cabalmente con las directrices oficiales de Meta Platforms, Inc., incluyendo sin limitación:
          </p>
          <ul className="list-disc pl-5 space-y-2 text-slate-700">
            <li>
              <strong>Política de WhatsApp Business (WhatsApp Business Policy):</strong> Prohibición absoluta de enviar mensajes no solicitados (SPAM), acoso, desinformación o esquemas fraudulentos. El Cliente garantiza que ha recabado el consentimiento expreso previo (<em>opt-in</em>) de cada usuario final antes de contactarlo.
            </li>
            <li>
              <strong>Política de Comercio de WhatsApp (WhatsApp Commerce Policy):</strong> Prohibición estricta de promoción o venta de productos y servicios no autorizados, tales como armas, municiones, sustancias ilícitas, fármacos con receta, productos de tabaco, juegos de azar ilegales y bienes falsificados.
            </li>
            <li>
              <strong>Calidad del Número y Tier de Mensajería:</strong> El Cliente es el único responsable de preservar una calificación de calidad verde o media en Meta. IridiaChat no responde por suspensiones, bloqueos o degradaciones de límites aplicadas discrecionalmente por Meta debido a reportes o bloqueos de usuarios finales.
            </li>
          </ul>
        </section>

        {/* 3. Conexión de Cuentas (Embedded Signup) */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">3</span>
            Registro Incrustado (Embedded Signup) y Permisos Compartidos
          </h2>
          <p>
            Para la vinculación masiva y automática con WhatsApp Business, la Plataforma implementa el flujo oficial de Registro Incrustado (<em>Embedded Signup</em>) provisto por Meta. Mediante este mecanismo:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>El Cliente autoriza a IridiaChat en calidad de <strong>Tech Provider</strong> a acceder a su cuenta de WhatsApp Business para registrar números, gestionar plantillas y enrutar mensajes a través de Webhooks.</li>
            <li>El Cliente retiene en todo momento la titularidad de su número y de su Business Portfolio en Meta, pudiendo desvincular o revocar el acceso en cualquier momento.</li>
          </ul>
        </section>

        {/* 4. Modelo de Facturación y Separación de Costos */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">4</span>
            Modelo de Facturación y Pagos
          </h2>
          <p>
            Para garantizar máxima transparencia y evitar sobreprecios en telecomunicaciones, la operación económica se rige por una estricta separación de cobros:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h3 className="font-semibold text-slate-900 mb-1">Costo de Mensajería WhatsApp</h3>
              <p className="text-xs sm:text-sm text-slate-600">
                Es cobrado <strong>directamente por Meta Platforms, Inc.</strong> al Cliente mediante la tarjeta de crédito o método de pago asociado a su cuenta de WhatsApp Business (WABA). IridiaChat no actúa como revendedor de mensajes ni aplica márgenes sobre la tarifa oficial de Meta.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h3 className="font-semibold text-slate-900 mb-1">Suscripción e Inteligencia Artificial</h3>
              <p className="text-xs sm:text-sm text-slate-600">
                Es facturado por <strong>IridiaChat</strong> conforme al plan contratado (mensual/anual), cubriendo el uso del software, atención en equipo (cola compartida), alojamiento e infraestructura de cómputo/créditos de IA consumidos en las respuestas del bot.
              </p>
            </div>
          </div>
        </section>

        {/* 5. Uso de Inteligencia Artificial y Supervisión Humana */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">5</span>
            Uso de Inteligencia Artificial y Descargo de Responsabilidad
          </h2>
          <p>
            IridiaChat integra modelos de lenguaje avanzado (LLM) para automatizar respuestas conversacionales y guiar a los prospectos en el proceso comercial. El Cliente acepta y reconoce expresamente que:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Las respuestas generadas por los bots automatizados tienen carácter asistencial y se basan en el catálogo y configuración provista por el Cliente.</li>
            <li>En sectores regulados (como salud, servicios médicos o legal), los bots operan estrictamente en modo informativo y de triaje previo, quedando prohibida la emisión de diagnósticos automatizados sin validación médica.</li>
            <li>La Plataforma provee mecanismos de transferencia inmediata a tu equipo humano (Human-in-the-Loop) cuando el usuario o el flujo lo requieran.</li>
          </ul>
        </section>

        {/* 6. Cancelación y Eliminación de Cuenta */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">6</span>
            Cancelación del Servicio y Eliminación de Datos
          </h2>
          <p>
            El Cliente tiene derecho a dar de baja su suscripción y eliminar su cuenta en cualquier momento:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Puede ejecutar la supresión de su cuenta directamente desde el panel de control en <em>Mi Perfil &gt; Zona de Peligro</em>.</li>
            <li>Puede solicitar la supresión de datos y desvinculación a través de nuestra herramienta pública en <a href="/eliminacion-datos" className="text-emerald-700 font-semibold underline">iridia-chat.juntaclick.com/eliminacion-datos</a>.</li>
            <li>La eliminación de la cuenta conlleva el cese inmediato de los webhooks, la revocación de tokens de acceso y el borrado seguro o anonimización de la información asociada conforme a nuestra Política de Privacidad.</li>
          </ul>
        </section>

        {/* 7. Limitación de Responsabilidad */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">7</span>
            Limitación de Responsabilidad
          </h2>
          <p>
            IridiaChat no será responsable por interrupciones de servicio originadas por caídas globales de la infraestructura de Meta, proveedores de telecomunicaciones o incidencias de fuerza mayor. En ningún caso IridiaChat responderá por lucro cesante, daños indirectos o pérdidas de ventas derivadas del uso de la Plataforma.
          </p>
        </section>

        {/* 8. Contacto */}
        <section className="space-y-3 pt-4 border-t border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">Contacto y Asesoría Legal</h2>
          <p>
            Para consultas relacionadas con estos Términos de Servicio, puede comunicarse con nuestro departamento legal y de soporte en:
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 inline-block">
            <p className="font-medium text-slate-900">IridiaChat / JuntaClick</p>
            <p className="text-slate-600 text-sm">Correo: soporte@juntaclick.com</p>
            <p className="text-slate-600 text-sm">Portal: iridia-chat.juntaclick.com</p>
          </div>
        </section>
      </div>
    </LegalLayout>
  )
}
